import { Box, Tab, Tabs, Typography } from '@mui/material';
import { find, map, multiply, sum, sumBy } from 'lodash-es';
import React, { useMemo } from 'react';
import { DAYS_PER_YEAR } from '~/constants/date';
import { date } from '~/lib/date';
import { calculateDailyEnergy } from '~/models/common';
import { SiteClassification } from '~/models/meter';
import { calculateWeightedRatesByEnergyFromSegments } from '../weightedRateUtils';
import BillSection from './BillSection';
import { calculateDiffInPercent } from './calculationUtils';
import {
  clipEnvironmentalSegmentsToYearTab,
  clippedYearTabEnd,
  expectedContractEndDate,
  formatYearTabDateRange,
  networkChargesBreakdownForDays,
  numberOfYearTabsForOfferPeriod,
  sumNetworkChargesForDays,
  yearTabStart
} from './currentVsNewYearSlice';
import { getLossFactorFromGreenBilling } from './eaReportCalculations';
import { calculateMeterConsumption, calculateMeterConsumptionByComponent } from './meterOfferCalculationUtils';
import { CalculatedProjectOfferPeriodsByName, EAReportGroupedMeter } from './reportTypes';
import {
  CenterAlignedSectionContainerStyle,
  CurrentContractCaptionStyle,
  Section,
  SectionContainer,
  Title,
  TitleUnderline,
  YearTabDateContainerStyle
} from './style';

const CurrentVsNewBillSection: React.FC<{ groupedMeter: EAReportGroupedMeter; reportType: string; name: string }> = ({
  groupedMeter,
  reportType,
  name
}) => {
  const [currentVsNewTab, setCurrentVsNewTab] = React.useState(0);
  const bestOffer = find(groupedMeter.offers, (offer) => offer.isBest);
  // Reference period for dates/year tabs; when meterPeriods exists use first meter's period so per-meter rate never uses "shared"
  const bestOfferPeriod = (() => {
    const mp = groupedMeter.meterPeriods;
    if (mp && groupedMeter.meters.length > 0) {
      const first = mp[groupedMeter.meters[0].id];
      if (first?.environmentalRatePeriods?.length) return first;
    }
    const period = find(bestOffer?.periods, (period) => period.isBest);
    if (period?.environmentalRatePeriods?.length) return period;
    const mapping = groupedMeter.meterOfferPeriodMapping;
    if (mapping && groupedMeter.meters.length > 0) {
      const recId = mapping[groupedMeter.meters[0].id]?.recommendedPeriodId;
      return (
        (groupedMeter.offers || []).flatMap((o) => o.periods || []).find((p: { id?: string }) => p.id === recId) ??
        ({} as CalculatedProjectOfferPeriodsByName)
      );
    }
    return {} as CalculatedProjectOfferPeriodsByName;
  })();

  // Calculate lossFactor for new contract based on offer's greenBilling (same as in calculateSingleMeterPeriodCosts)
  const newContractLossFactor = getLossFactorFromGreenBilling(
    bestOffer?.greenBilling,
    groupedMeter.lwaDLF,
    groupedMeter.lwaMLF
  );
  const divide = reportType === 'ANNUALLY' ? 1 : 1 / 12;

  // Tab count follows the same `bestOfferPeriod` as `getNewContract` (periodName months, else calendar span).
  const meters = groupedMeter.meters;
  const numberOfYearTabs = numberOfYearTabsForOfferPeriod(bestOfferPeriod);
  const isTariff = meters[0]?.contract?.contractType === SiteClassification.TARIFF;
  const peakEnergy = sumBy(meters, 'annualData.peakEnergy');
  const offPeakEnergy = sumBy(meters, 'annualData.offPeakEnergy');
  const shoulderEnergy = sumBy(meters, 'annualData.shoulderEnergy');

  const calculateEnergyCharge = (energy: number, rate: number) =>
    (energy * rate * (isTariff ? 1 : groupedMeter.lwaDLF * groupedMeter.lwaMLF)) / 100;

  // Weighted regulated rates from annual meter data (used by current bill builder)
  const totalEnergyIncLossCurrent = sum(
    meters.map((m) => calculateMeterConsumption(m, DAYS_PER_YEAR, { usePerMeterLossFactor: true }))
  );
  const participantSubTotalCurrent = sum(
    meters.map((m) => {
      const lf = getLossFactorFromGreenBilling(
        m.contract?.retailer?.greenBilling,
        m.dlf ?? m.tlf ?? 1,
        m.mlf ?? m.tlf ?? 1
      );
      return ((m.participantCharge ?? 0) * (m.annualData?.totalEnergy ?? 0) * lf) / 100;
    })
  );
  const ancillarySubTotalCurrent = sum(
    meters.map((m) => {
      const lf = getLossFactorFromGreenBilling(
        m.contract?.retailer?.greenBilling,
        m.dlf ?? m.tlf ?? 1,
        m.mlf ?? m.tlf ?? 1
      );
      return ((m.ancillaryCharge ?? 0) * (m.annualData?.totalEnergy ?? 0) * lf) / 100;
    })
  );
  const participantRateWeighted =
    totalEnergyIncLossCurrent > 0 ? (participantSubTotalCurrent / totalEnergyIncLossCurrent) * 100 : 0;
  const ancillaryRateWeighted =
    totalEnergyIncLossCurrent > 0 ? (ancillarySubTotalCurrent / totalEnergyIncLossCurrent) * 100 : 0;

  // Calculate year summaries for tab display.
  // Only include a year if every meter in the group has at least one offer segment in that year's range.
  const yearSummaries = useMemo(() => {
    const mapping = groupedMeter.meterOfferPeriodMapping;
    const allOffers = groupedMeter.offers || [];
    const mp = groupedMeter.meterPeriods;

    // Same reference period as the new-contract detail (`getNewContract`) so tab strip matches each panel.
    if (
      numberOfYearTabs === 0 ||
      !bestOfferPeriod?.environmentalRatePeriods ||
      bestOfferPeriod.environmentalRatePeriods.length === 0
    )
      return [];

    const contractStartDate = bestOfferPeriod.startDate ?? bestOfferPeriod.environmentalRatePeriods[0]?.startDate ?? '';
    if (!contractStartDate) return [];

    const contractEnd = expectedContractEndDate(bestOfferPeriod);
    const allPeriods = bestOfferPeriod.environmentalRatePeriods;
    const totalAvailableDays = sumBy(allPeriods, 'numOfDays');

    // Helper: whether this meter has at least one segment in the given year range [yearStart, yearEnd)
    const meterHasSegmentInYearRange = (meterId: string, yearIndex: number): boolean => {
      const yearStart = yearTabStart(contractStartDate, yearIndex);
      // Same exclusive right edge as `getNewContract` / clip (nominal tab end capped by contract +1 day).
      const yearEnd = clippedYearTabEnd(contractStartDate, yearIndex, contractEnd);
      const yearStartTs = yearStart.valueOf();
      const yearEndTs = yearEnd.valueOf();
      let periods: { startDate?: string; endDate?: string }[] | null = null;
      const perMeterPeriod = mp?.[meterId];
      if (perMeterPeriod?.environmentalRatePeriods?.length) {
        periods = perMeterPeriod.environmentalRatePeriods;
      } else if (mapping?.[meterId]?.recommendedPeriodId) {
        const recId = mapping[meterId].recommendedPeriodId;
        for (const offer of allOffers) {
          const p = (offer.periods || []).find((x: { id?: string }) => x.id === recId);
          if (p?.environmentalRatePeriods?.length) {
            periods = p.environmentalRatePeriods;
            break;
          }
        }
      }
      if (!periods?.length) return false;
      return periods.some((seg) => {
        const segStart = date(seg.startDate || '').valueOf();
        const segEnd = date(seg.endDate || '').valueOf();
        return segEnd > yearStartTs && segStart < yearEndTs;
      });
    };

    // Only years where every meter has an offer segment in that year's range
    const validYearIndices = [...Array(numberOfYearTabs)]
      .map((_, i) => i)
      .filter((yearIndex) => meters.every((m) => meterHasSegmentInYearRange(m.id, yearIndex)));

    return validYearIndices.map((yearIndex) => {
      const yearStartDate = yearTabStart(contractStartDate, yearIndex);
      const actualYearEndDate = clippedYearTabEnd(contractStartDate, yearIndex, contractEnd);
      const { tabStartFormatted, tabEndFormatted } = formatYearTabDateRange(contractStartDate, yearIndex, contractEnd);

      // Tab starts after the last inclusive contract calendar day → no slice (compare by date, not midnight equality).
      if (yearStartDate.isAfter(contractEnd, 'day')) {
        return {
          yearIndex,
          totalCost: 0,
          diff: 0,
          diffInPercent: 0,
          tabStartFormatted,
          tabEndFormatted
        };
      }

      const yearDays = Math.ceil((actualYearEndDate.valueOf() - yearStartDate.valueOf()) / (1000 * 60 * 60 * 24));

      const clippedForSummary =
        !mapping && allPeriods.length > 0
          ? clipEnvironmentalSegmentsToYearTab(allPeriods, yearStartDate, actualYearEndDate)
          : [];

      let contestableCost: number;
      let regulatedCost: number;
      let totalNumOfDays: number;

      if (clippedForSummary.length > 0) {
        totalNumOfDays = sumBy(clippedForSummary, 'numOfDays');
        contestableCost = sumBy(clippedForSummary, 'contestableCost');
        regulatedCost = sumBy(clippedForSummary, 'regulatedCost');
      } else {
        // Mix-and-match (mapping) or no segment data: use period totals, allocate by days
        const totalContestableCost = mapping
          ? (bestOfferPeriod as { periodContestableCosts?: number }).periodContestableCosts ?? 0
          : sumBy(allPeriods, 'contestableCost');
        const totalRegulatedCost = mapping
          ? (bestOfferPeriod as { periodRegulatedCost?: number }).periodRegulatedCost ?? 0
          : sumBy(allPeriods, 'regulatedCost');

        const daysForAllocation = mapping
          ? (bestOfferPeriod as { totalNumOfDays?: number }).totalNumOfDays || totalAvailableDays
          : totalAvailableDays;
        const avgDailyContestableCost = daysForAllocation > 0 ? totalContestableCost / daysForAllocation : 0;
        const avgDailyRegulatedCost = daysForAllocation > 0 ? totalRegulatedCost / daysForAllocation : 0;

        totalNumOfDays = yearDays;
        contestableCost = avgDailyContestableCost * yearDays;
        regulatedCost = avgDailyRegulatedCost * yearDays;
      }

      const networkForTab = sumNetworkChargesForDays(groupedMeter.meters, totalNumOfDays, divide);

      const yearTotalCost = sum([contestableCost * divide, regulatedCost * divide, networkForTab]);

      const currentTotalCost = sum([
        (groupedMeter.currentContestableCost / DAYS_PER_YEAR) * totalNumOfDays * divide,
        (groupedMeter.currentRegulatedCost / DAYS_PER_YEAR) * totalNumOfDays * divide,
        networkForTab
      ]);

      const diff = yearTotalCost - currentTotalCost;
      const diffInPercent = calculateDiffInPercent(currentTotalCost, yearTotalCost);

      return { yearIndex, totalCost: yearTotalCost, diff, diffInPercent, tabStartFormatted, tabEndFormatted };
    });
  }, [bestOfferPeriod, numberOfYearTabs, divide, groupedMeter]);

  const numOfMetersCount = sumBy(groupedMeter.meters, 'numOfMeters');
  const usePerMeterDisplay = numOfMetersCount > 1;

  /**
   * Current contract bill for `numOfDays` calendar days (annual slice / tab window), then scaled by `divide` for the report.
   * Tab 0 uses `DAYS_PER_YEAR`; each year tab uses the clipped new-offer window length.
   */
  const buildCurrentContractForDays = (totalNumOfDays: number) => {
    const days = totalNumOfDays > 0 ? totalNumOfDays : DAYS_PER_YEAR;
    const dayRatio = days / DAYS_PER_YEAR;

    const tabEnergyPerMeter = (() => {
      let peakSubTotal = 0;
      let offPeakSubTotal = 0;
      let shoulderSubTotal = 0;
      let peakEnergyIncLoss = 0;
      let offPeakEnergyIncLoss = 0;
      let shoulderEnergyIncLoss = 0;
      meters.forEach((meter) => {
        const comp = calculateMeterConsumptionByComponent(meter, days, {
          usePerMeterLossFactor: true
        });
        const peakRate = meter.contractPeriod?.peakRate ?? 0;
        const offPeakRate = meter.contractPeriod?.offPeakRate ?? 0;
        const shoulderRate = meter.contractPeriod?.shoulderRate ?? 0;
        peakSubTotal += (comp.peak * peakRate) / 100;
        offPeakSubTotal += (comp.offPeak * offPeakRate) / 100;
        shoulderSubTotal += (comp.shoulder * shoulderRate) / 100;
        peakEnergyIncLoss += comp.peak;
        offPeakEnergyIncLoss += comp.offPeak;
        shoulderEnergyIncLoss += comp.shoulder;
      });
      return {
        peakSubTotal,
        offPeakSubTotal,
        shoulderSubTotal,
        peakRateWeighted: peakEnergyIncLoss > 0 ? (peakSubTotal / peakEnergyIncLoss) * 100 : 0,
        offPeakRateWeighted: offPeakEnergyIncLoss > 0 ? (offPeakSubTotal / offPeakEnergyIncLoss) * 100 : 0,
        shoulderRateWeighted: shoulderEnergyIncLoss > 0 ? (shoulderSubTotal / shoulderEnergyIncLoss) * 100 : 0,
        peakEnergyIncLoss,
        offPeakEnergyIncLoss,
        shoulderEnergyIncLoss,
        energySubTotal: peakSubTotal + offPeakSubTotal + shoulderSubTotal
      };
    })();

    const tabTotalEnergyIncLoss = sum(
      meters.map((m) => calculateMeterConsumption(m, days, { usePerMeterLossFactor: true }))
    );

    const envSubTotalForTab = (
      rateKey: 'lretRate' | 'srecRate' | 'veetRate' | 'essRate' | 'aeeisRate' | 'pdrsRate' | 'repsRate'
    ) =>
      sum(
        meters.map((m) => {
          const rate = (m.contractPeriod as unknown as Record<string, number | undefined> | undefined)?.[rateKey] ?? 0;
          const consumption = calculateMeterConsumption(m, days, { usePerMeterLossFactor: true });
          return (rate * consumption) / 100;
        })
      );

    const lretTab = envSubTotalForTab('lretRate');
    const sresTab = envSubTotalForTab('srecRate');
    const veetTab = envSubTotalForTab('veetRate');
    const essTab = envSubTotalForTab('essRate');
    const aeeisTab = envSubTotalForTab('aeeisRate');
    const pdrsTab = envSubTotalForTab('pdrsRate');
    const repsTab = envSubTotalForTab('repsRate');
    const renewableTab = lretTab + sresTab + veetTab + essTab + aeeisTab + pdrsTab + repsTab;
    const networkTab = networkChargesBreakdownForDays(groupedMeter.meters, days, divide);
    const wad = groupedMeter.weighedAverageData;
    // Align env $ with `calculateGroupedMeterData` (eaReportCalculations): renewable uses totalEnergy×lossFactor there,
    // while line build uses rate×calculateMeterConsumption — split can drift from `currentRenewableCost` inside contestable.
    const renewableSubTotalAligned = groupedMeter.currentRenewableCost * dayRatio * divide;
    const envAmountShare = (costTab: number) =>
      renewableTab > 0 ? (costTab / renewableTab) * renewableSubTotalAligned : 0;
    const envRateDisplay = (costTab: number, summaryRate: number) =>
      usePerMeterDisplay
        ? tabTotalEnergyIncLoss > 0
          ? (costTab / tabTotalEnergyIncLoss) * 100
          : summaryRate
        : summaryRate;

    const contractPeakEnergyTab = calculateDailyEnergy(peakEnergy) * days;
    const contractOffPeakEnergyTab = calculateDailyEnergy(offPeakEnergy) * days;
    const contractShoulderEnergyTab = calculateDailyEnergy(shoulderEnergy) * days;

    return {
      lwaMLF: groupedMeter.lwaMLF,
      lwaDLF: groupedMeter.lwaDLF,
      lossFactor: groupedMeter.lossFactor,
      greenBilling: groupedMeter.meters[0]?.contract?.retailer?.greenBilling,
      peakEnergy: contractPeakEnergyTab * divide,
      offPeakEnergy: contractOffPeakEnergyTab * divide,
      shoulderEnergy: contractShoulderEnergyTab * divide,
      totalEnergy: groupedMeter.annualData.totalEnergy * dayRatio * divide,
      peakEnergyIncLoss: usePerMeterDisplay ? tabEnergyPerMeter.peakEnergyIncLoss * divide : undefined,
      offPeakEnergyIncLoss: usePerMeterDisplay ? tabEnergyPerMeter.offPeakEnergyIncLoss * divide : undefined,
      shoulderEnergyIncLoss: usePerMeterDisplay ? tabEnergyPerMeter.shoulderEnergyIncLoss * divide : undefined,
      totalEnergyIncLoss: usePerMeterDisplay ? tabTotalEnergyIncLoss * divide : undefined,
      contestableCost: groupedMeter.currentContestableCost * dayRatio * divide,
      energyCharge: {
        isTariff,
        peakRate: usePerMeterDisplay ? tabEnergyPerMeter.peakRateWeighted : groupedMeter.weighedAverageData.peakRate,
        peakSubTotal:
          (usePerMeterDisplay
            ? tabEnergyPerMeter.peakSubTotal
            : calculateEnergyCharge(contractPeakEnergyTab, groupedMeter.weighedAverageData.peakRate)) * divide,
        offPeakRate: usePerMeterDisplay
          ? tabEnergyPerMeter.offPeakRateWeighted
          : groupedMeter.weighedAverageData.offPeakRate,
        offPeakSubTotal:
          (usePerMeterDisplay
            ? tabEnergyPerMeter.offPeakSubTotal
            : calculateEnergyCharge(contractOffPeakEnergyTab, groupedMeter.weighedAverageData.offPeakRate)) * divide,
        shoulderRate: usePerMeterDisplay
          ? tabEnergyPerMeter.shoulderRateWeighted
          : groupedMeter.weighedAverageData.shoulderRate,
        shoulderSubTotal:
          (usePerMeterDisplay
            ? tabEnergyPerMeter.shoulderSubTotal
            : calculateEnergyCharge(contractShoulderEnergyTab, groupedMeter.weighedAverageData.shoulderRate)) * divide,
        subTotal:
          (usePerMeterDisplay
            ? tabEnergyPerMeter.energySubTotal
            : calculateEnergyCharge(contractPeakEnergyTab, groupedMeter.weighedAverageData.peakRate) +
              calculateEnergyCharge(contractOffPeakEnergyTab, groupedMeter.weighedAverageData.offPeakRate) +
              calculateEnergyCharge(contractShoulderEnergyTab, groupedMeter.weighedAverageData.shoulderRate)) * divide
      },
      networkCharge: networkTab,
      regulatedCharge: (() => {
        const pRate = usePerMeterDisplay ? participantRateWeighted : groupedMeter.weighedAverageData.participantCharge;
        const aRate = usePerMeterDisplay ? ancillaryRateWeighted : groupedMeter.weighedAverageData.ancillaryCharge;
        const qty = usePerMeterDisplay
          ? tabTotalEnergyIncLoss * divide
          : groupedMeter.annualData.totalEnergy * groupedMeter.lossFactor * dayRatio * divide;
        const subTotal = ((pRate + aRate) * qty) / 100;
        return { participantRate: pRate, ancillaryRate: aRate, subTotal };
      })(),
      environmentalCharge: {
        lrecRate: envRateDisplay(lretTab, wad.lret),
        lrecSubTotal: envAmountShare(lretTab),
        sresRate: envRateDisplay(sresTab, wad.sres),
        sresSubTotal: envAmountShare(sresTab),
        veetRate: envRateDisplay(veetTab, wad.veet),
        veetSubTotal: envAmountShare(veetTab),
        essRate: envRateDisplay(essTab, wad.ess),
        essSubTotal: envAmountShare(essTab),
        aeeisRate: envRateDisplay(aeeisTab, wad.aeeis),
        aeeisSubTotal: envAmountShare(aeeisTab),
        pdrsRate: envRateDisplay(pdrsTab, wad.pdrs),
        pdrsSubTotal: envAmountShare(pdrsTab),
        repsRate: envRateDisplay(repsTab, wad.reps),
        repsSubTotal: envAmountShare(repsTab),
        subTotal: renewableSubTotalAligned
      },
      meteringAndRetailerCharge: {
        numOfMeters: sumBy(groupedMeter.meters, 'numOfMeters'),
        meteringChargeRate: groupedMeter.weighedAverageData.annualMeteringCharge * divide,
        meteringCharge: groupedMeter.annualData.meteringCharge * dayRatio * divide,
        retailerFeeRate: groupedMeter.weighedAverageData.annualRetailerFee * divide,
        retailerFee: groupedMeter.annualData.retailerFee * dayRatio * divide,
        subTotal: sum([groupedMeter.annualData.meteringCharge, groupedMeter.annualData.retailerFee]) * dayRatio * divide
      }
    };
  };

  const currentContract = buildCurrentContractForDays(DAYS_PER_YEAR);

  const getNewContract = (yearIndex: number) => {
    if (
      !bestOfferPeriod ||
      !bestOfferPeriod.environmentalRatePeriods ||
      bestOfferPeriod.environmentalRatePeriods.length === 0
    ) {
      return null;
    }

    const contractStartDate = bestOfferPeriod.startDate ?? bestOfferPeriod.environmentalRatePeriods[0]?.startDate ?? '';
    if (!contractStartDate) {
      return null;
    }

    const contractEnd = expectedContractEndDate(bestOfferPeriod);
    const yearStart = yearTabStart(contractStartDate, yearIndex);
    const yearEnd = clippedYearTabEnd(contractStartDate, yearIndex, contractEnd);

    type SegmentLike = typeof bestOfferPeriod.environmentalRatePeriods[number];
    const clipSegmentsToYear = (segments: SegmentLike[]) =>
      clipEnvironmentalSegmentsToYearTab(segments, yearStart, yearEnd);

    const periods = clipSegmentsToYear(bestOfferPeriod.environmentalRatePeriods);

    const totalNumOfDays = sum(periods.map((period) => period.numOfDays));

    const contractPeakEnergy = calculateDailyEnergy(peakEnergy) * totalNumOfDays;
    const contractOffPeakEnergy = calculateDailyEnergy(offPeakEnergy) * totalNumOfDays;
    const contractShoulderEnergy = calculateDailyEnergy(shoulderEnergy) * totalNumOfDays;
    const contractTotalEnergy = contractPeakEnergy + contractOffPeakEnergy + contractShoulderEnergy;

    // Calculate weighted environmental rates (same as old version)
    const weighedEnvironmentalRates = {
      lret: sumBy(periods, (period) => multiply(period.lret ?? 0, period.numOfDays)) / (totalNumOfDays || 1),
      sres: sumBy(periods, (period) => multiply(period.sres ?? 0, period.numOfDays)) / (totalNumOfDays || 1),
      veet: sumBy(periods, (period) => multiply(period.veet ?? 0, period.numOfDays)) / (totalNumOfDays || 1),
      ess: sumBy(periods, (period) => multiply(period.ess ?? 0, period.numOfDays)) / (totalNumOfDays || 1),
      aeeis: sumBy(periods, (period) => multiply(period.aeeis ?? 0, period.numOfDays)) / (totalNumOfDays || 1),
      pdrs: sumBy(periods, (period) => multiply(period.pdrs ?? 0, period.numOfDays)) / (totalNumOfDays || 1),
      reps: sumBy(periods, (period) => multiply(period.reps ?? 0, period.numOfDays)) / (totalNumOfDays || 1)
    };

    // Use shared weighted rate (ConsumptionIncLoss when present, else dailyEnergy × numOfDays)
    const dailyPeak = calculateDailyEnergy(peakEnergy);
    const dailyOffPeak = calculateDailyEnergy(offPeakEnergy);
    const dailyShoulder = calculateDailyEnergy(shoulderEnergy);
    const {
      peak: weightedPeakRate,
      offPeak: weightedOffPeakRate,
      shoulder: weightedShoulderRate
    } = calculateWeightedRatesByEnergyFromSegments(periods, dailyPeak, dailyOffPeak, dailyShoulder);

    const newContractEnergyLossFactor = isTariff ? 1 : newContractLossFactor;
    const newContractPeakEnergyIncLoss = usePerMeterDisplay
      ? sum(
          meters.map(
            (m) => calculateMeterConsumptionByComponent(m, totalNumOfDays, { usePerMeterLossFactor: true }).peak
          )
        )
      : contractPeakEnergy * newContractEnergyLossFactor;
    const newContractOffPeakEnergyIncLoss = usePerMeterDisplay
      ? sum(
          meters.map(
            (m) => calculateMeterConsumptionByComponent(m, totalNumOfDays, { usePerMeterLossFactor: true }).offPeak
          )
        )
      : contractOffPeakEnergy * newContractEnergyLossFactor;
    const newContractShoulderEnergyIncLoss = usePerMeterDisplay
      ? sum(
          meters.map(
            (m) => calculateMeterConsumptionByComponent(m, totalNumOfDays, { usePerMeterLossFactor: true }).shoulder
          )
        )
      : contractShoulderEnergy * newContractEnergyLossFactor;
    const newContractTotalEnergyIncLoss = usePerMeterDisplay
      ? sum(meters.map((m) => calculateMeterConsumption(m, totalNumOfDays, { usePerMeterLossFactor: true })))
      : contractTotalEnergy * newContractEnergyLossFactor;

    const networkForYear = networkChargesBreakdownForDays(groupedMeter.meters, totalNumOfDays, divide);

    const newContract = {
      lwaMLF: groupedMeter.lwaMLF,
      lwaDLF: groupedMeter.lwaDLF,
      lossFactor: newContractLossFactor,
      greenBilling: bestOffer?.greenBilling,
      peakEnergy: contractPeakEnergy * divide,
      offPeakEnergy: contractOffPeakEnergy * divide,
      shoulderEnergy: contractShoulderEnergy * divide,
      totalEnergy: contractTotalEnergy * divide,
      peakEnergyIncLoss: usePerMeterDisplay ? newContractPeakEnergyIncLoss * divide : undefined,
      offPeakEnergyIncLoss: usePerMeterDisplay ? newContractOffPeakEnergyIncLoss * divide : undefined,
      shoulderEnergyIncLoss: usePerMeterDisplay ? newContractShoulderEnergyIncLoss * divide : undefined,
      totalEnergyIncLoss: usePerMeterDisplay ? newContractTotalEnergyIncLoss * divide : undefined,
      contestableCost: usePerMeterDisplay
        ? (() => {
            const mp = groupedMeter.meterPeriods;
            const mapping = groupedMeter.meterOfferPeriodMapping;
            const allOffers = groupedMeter.offers || [];
            let total = 0;
            meters.forEach((m) => {
              const perMeterPeriod = mp?.[m.id];
              let segs: { contestableCost?: number; meterContestableCosts?: Record<string, number> }[] = [];
              if (perMeterPeriod?.environmentalRatePeriods?.length) {
                segs = clipSegmentsToYear(perMeterPeriod.environmentalRatePeriods);
              } else if (mapping?.[m.id]?.recommendedPeriodId) {
                const recId = mapping[m.id].recommendedPeriodId;
                for (const offer of allOffers) {
                  const p = (offer.periods || []).find((x: { id?: string }) => x.id === recId);
                  if (p?.environmentalRatePeriods?.length) {
                    segs = clipSegmentsToYear(p.environmentalRatePeriods);
                    break;
                  }
                }
              }
              if (segs.length === 0) segs = periods;
              total += segs.reduce((sum, seg) => sum + (seg.meterContestableCosts?.[m.id] ?? 0), 0);
            });
            return total * divide;
          })()
        : sumBy(periods, 'contestableCost') * divide,
      energyCharge: (() => {
        if (usePerMeterDisplay) {
          let peakSubTotal = 0;
          let offPeakSubTotal = 0;
          let shoulderSubTotal = 0;

          const meterPeriods = groupedMeter.meterPeriods;
          const meterOfferPeriodMapping = groupedMeter.meterOfferPeriodMapping;
          const allOffers = groupedMeter.offers || [];

          // Each meter uses its own period from meterPeriods; segments are clipped to year (overlap + prorate)
          const getMeterSegmentsForYear = (
            meterId: string,
            _yearIdx: number
          ): {
            segments: { numOfDays: number; peak: number; offPeak: number; shoulder: number }[];
            periodSource: string;
          } => {
            const toSegmentRow = (s: { numOfDays?: number; peak?: number; offPeak?: number; shoulder?: number }) => ({
              numOfDays: s.numOfDays ?? 0,
              peak: s.peak ?? 0,
              offPeak: s.offPeak ?? 0,
              shoulder: s.shoulder ?? 0
            });
            const perMeterPeriod = meterPeriods?.[meterId];
            if (perMeterPeriod?.environmentalRatePeriods?.length) {
              const segs = clipSegmentsToYear(perMeterPeriod.environmentalRatePeriods);
              const recId = meterOfferPeriodMapping?.[meterId]?.recommendedPeriodId ?? '';
              return {
                segments: segs.map((s) => toSegmentRow(s)),
                periodSource: `meter's period (recommendedPeriodId=${recId})`
              };
            }
            if (meterOfferPeriodMapping?.[meterId]?.recommendedPeriodId) {
              const recPeriodId = meterOfferPeriodMapping[meterId].recommendedPeriodId;
              for (const offer of allOffers) {
                const period = (offer.periods || []).find((p: { id?: string }) => p.id === recPeriodId);
                if (period?.environmentalRatePeriods?.length) {
                  const segs = clipSegmentsToYear(period.environmentalRatePeriods);
                  if (segs.length > 0) {
                    return {
                      segments: segs.map((s) => toSegmentRow(s)),
                      periodSource: `meter's period (recommendedPeriodId=${recPeriodId}, retailer=${
                        (offer as { retailer?: { name?: string } }).retailer?.name ?? '?'
                      })`
                    };
                  }
                }
              }
            }
            return {
              segments: periods.map((p) => toSegmentRow(p)),
              periodSource: 'bestOfferPeriod (shared by all meters)'
            };
          };

          meters.forEach((m) => {
            const { segments: meterSegments } = getMeterSegmentsForYear(m.id, yearIndex);

            meterSegments.forEach((seg) => {
              const comp = calculateMeterConsumptionByComponent(m, seg.numOfDays, {
                usePerMeterLossFactor: true
              });
              peakSubTotal += (comp.peak * seg.peak) / 100;
              offPeakSubTotal += (comp.offPeak * seg.offPeak) / 100;
              shoulderSubTotal += (comp.shoulder * seg.shoulder) / 100;
            });
          });
          const subTotal = (peakSubTotal + offPeakSubTotal + shoulderSubTotal) * divide;
          const result = {
            isTariff,
            peakRate: weightedPeakRate,
            peakSubTotal: peakSubTotal * divide,
            offPeakRate: weightedOffPeakRate,
            offPeakSubTotal: offPeakSubTotal * divide,
            shoulderRate: weightedShoulderRate,
            shoulderSubTotal: shoulderSubTotal * divide,
            subTotal
          };
          return result;
        }
        const subTotal = sumBy(periods, 'energyCost') * divide;
        const result = {
          isTariff,
          peakRate: weightedPeakRate,
          peakSubTotal: calculateEnergyCharge(contractPeakEnergy, weightedPeakRate) * divide,
          offPeakRate: weightedOffPeakRate,
          offPeakSubTotal: calculateEnergyCharge(contractOffPeakEnergy, weightedOffPeakRate) * divide,
          shoulderRate: weightedShoulderRate,
          shoulderSubTotal: calculateEnergyCharge(contractShoulderEnergy, weightedShoulderRate) * divide,
          subTotal
        };
        return result;
      })(),
      networkCharge: networkForYear,
      regulatedCharge: (() => {
        const pRate = usePerMeterDisplay ? participantRateWeighted : groupedMeter.weighedAverageData.participantCharge;
        const aRate = usePerMeterDisplay ? ancillaryRateWeighted : groupedMeter.weighedAverageData.ancillaryCharge;
        // Segment regulatedCost in eaReportCalculations is sumBy(offerMeterCosts, 'regulatedCost'). Sum once; do NOT sum per meter.
        const regulatedTotal = sumBy(periods, 'regulatedCost') * divide;
        const subTotal = regulatedTotal;
        // Row amounts from segment-sum split by rate ratio so Total = sum of rows (align with Best Offer / Summary)
        const rateSum = pRate + aRate;
        const participantSubTotal = rateSum > 0 ? regulatedTotal * (pRate / rateSum) : undefined;
        const ancillarySubTotal = rateSum > 0 ? regulatedTotal * (aRate / rateSum) : undefined;
        return {
          participantRate: pRate,
          ancillaryRate: aRate,
          subTotal,
          participantSubTotal,
          ancillarySubTotal
        };
      })(),
      environmentalCharge: (() => {
        // Segment renewableCost in eaReportCalculations is already sumBy(offerMeterCosts, 'renewableCost'),
        // i.e. total for all meters in the offer. Sum segments once; do NOT sum per meter or we overcount by numOfMeters.
        const renewableSubTotal = sumBy(periods, 'renewableCost') * divide;
        const envRates = weighedEnvironmentalRates;
        // Row amounts = segment-sum (renewableSubTotal) split by weighted rate so Total = sum of rows (align with Best Offer / Summary)
        const envRateSum =
          (envRates.lret ?? 0) +
          (envRates.sres ?? 0) +
          (envRates.veet ?? 0) +
          (envRates.ess ?? 0) +
          (envRates.aeeis ?? 0) +
          (envRates.pdrs ?? 0) +
          (envRates.reps ?? 0);
        const envSubTotals =
          envRateSum > 0
            ? {
                lrec: (renewableSubTotal * (envRates.lret ?? 0)) / envRateSum,
                sres: (renewableSubTotal * (envRates.sres ?? 0)) / envRateSum,
                veet: (renewableSubTotal * (envRates.veet ?? 0)) / envRateSum,
                ess: (renewableSubTotal * (envRates.ess ?? 0)) / envRateSum,
                aeeis: (renewableSubTotal * (envRates.aeeis ?? 0)) / envRateSum,
                pdrs: (renewableSubTotal * (envRates.pdrs ?? 0)) / envRateSum,
                reps: (renewableSubTotal * (envRates.reps ?? 0)) / envRateSum
              }
            : {
                lrec: 0,
                sres: 0,
                veet: 0,
                ess: 0,
                aeeis: 0,
                pdrs: 0,
                reps: 0
              };
        return {
          lrecRate: envRates.lret ?? 0,
          lrecSubTotal: envSubTotals.lrec,
          sresRate: envRates.sres ?? 0,
          sresSubTotal: envSubTotals.sres,
          veetRate: envRates.veet ?? 0,
          veetSubTotal: envSubTotals.veet,
          essRate: envRates.ess ?? 0,
          essSubTotal: envSubTotals.ess,
          aeeisRate: envRates.aeeis ?? 0,
          aeeisSubTotal: envSubTotals.aeeis,
          pdrsRate: envRates.pdrs ?? 0,
          pdrsSubTotal: envSubTotals.pdrs,
          repsRate: envRates.reps ?? 0,
          repsSubTotal: envSubTotals.reps,
          subTotal: renewableSubTotal
        };
      })(),
      meteringAndRetailerCharge: (() => {
        // Metering/retailer price does not vary by segment; use totalNumOfDays once instead of summing per segment
        const numOfMeters = sumBy(groupedMeter.meters, 'numOfMeters');
        const dailyMetering = bestOffer?.dailyMeteringFee ?? 0;
        const dailyService = bestOffer?.dailyServiceFee ?? 0;
        const meteringCharge =
          bestOffer?.meteringFeePrice != null
            ? dailyMetering * totalNumOfDays * numOfMeters * divide
            : sumBy(
                groupedMeter.meters,
                (m) => (((m.annualData?.meteringChargePerMeter ?? 0) * totalNumOfDays) / DAYS_PER_YEAR) * m.numOfMeters
              ) * divide;
        const retailerFee =
          bestOffer?.serviceFeePrice != null
            ? dailyService * totalNumOfDays * numOfMeters * divide
            : sumBy(
                groupedMeter.meters,
                (m) => (((m.annualData?.retailerFeePerMeter ?? 0) * totalNumOfDays) / DAYS_PER_YEAR) * m.numOfMeters
              ) * divide;
        return {
          numOfMeters,
          meteringChargeRate: bestOffer?.dailyMeteringFee
            ? bestOffer.dailyMeteringFee * DAYS_PER_YEAR * divide
            : groupedMeter.weighedAverageData.annualMeteringCharge * divide,
          retailerFeeRate: bestOffer?.dailyServiceFee
            ? bestOffer.dailyServiceFee * DAYS_PER_YEAR * divide
            : groupedMeter.weighedAverageData.annualRetailerFee * divide,
          meteringCharge,
          retailerFee,
          subTotal: meteringCharge + retailerFee
        };
      })()
    };

    const currentForDiff = buildCurrentContractForDays(totalNumOfDays || DAYS_PER_YEAR);

    return (
      <BillSection
        data={{
          ...newContract,
          hasDiffValue: true,
          energyCharge: {
            ...newContract.energyCharge,
            diff: {
              peak: newContract.energyCharge.peakSubTotal - currentForDiff.energyCharge.peakSubTotal,
              offPeak: newContract.energyCharge.offPeakSubTotal - currentForDiff.energyCharge.offPeakSubTotal,
              shoulder: newContract.energyCharge.shoulderSubTotal - currentForDiff.energyCharge.shoulderSubTotal,
              subTotal: newContract.energyCharge.subTotal - currentForDiff.energyCharge.subTotal
            },
            diffInPercent: {
              peak: calculateDiffInPercent(
                currentForDiff.energyCharge.peakSubTotal,
                newContract.energyCharge.peakSubTotal
              ),
              offPeak: calculateDiffInPercent(
                currentForDiff.energyCharge.offPeakSubTotal,
                newContract.energyCharge.offPeakSubTotal
              ),
              shoulder: calculateDiffInPercent(
                currentForDiff.energyCharge.shoulderSubTotal,
                newContract.energyCharge.shoulderSubTotal
              ),
              subTotal: calculateDiffInPercent(currentForDiff.energyCharge.subTotal, newContract.energyCharge.subTotal)
            }
          },
          environmentalCharge: {
            ...newContract.environmentalCharge,
            diff: {
              lrec: newContract.environmentalCharge.lrecSubTotal - currentForDiff.environmentalCharge.lrecSubTotal,
              sres: newContract.environmentalCharge.sresSubTotal - currentForDiff.environmentalCharge.sresSubTotal,
              veet: newContract.environmentalCharge.veetSubTotal - currentForDiff.environmentalCharge.veetSubTotal,
              ess: newContract.environmentalCharge.essSubTotal - currentForDiff.environmentalCharge.essSubTotal,
              aeeis: newContract.environmentalCharge.aeeisSubTotal - currentForDiff.environmentalCharge.aeeisSubTotal,
              pdrs: newContract.environmentalCharge.pdrsSubTotal - currentForDiff.environmentalCharge.pdrsSubTotal,
              reps: newContract.environmentalCharge.repsSubTotal - currentForDiff.environmentalCharge.repsSubTotal,
              subTotal: newContract.environmentalCharge.subTotal - currentForDiff.environmentalCharge.subTotal
            },
            diffInPercent: {
              lrec: calculateDiffInPercent(
                currentForDiff.environmentalCharge.lrecSubTotal,
                newContract.environmentalCharge.lrecSubTotal
              ),
              sres: calculateDiffInPercent(
                currentForDiff.environmentalCharge.sresSubTotal,
                newContract.environmentalCharge.sresSubTotal
              ),
              veet: calculateDiffInPercent(
                currentForDiff.environmentalCharge.veetSubTotal,
                newContract.environmentalCharge.veetSubTotal
              ),
              ess: calculateDiffInPercent(
                currentForDiff.environmentalCharge.essSubTotal,
                newContract.environmentalCharge.essSubTotal
              ),
              aeeis: calculateDiffInPercent(
                currentForDiff.environmentalCharge.aeeisSubTotal,
                newContract.environmentalCharge.aeeisSubTotal
              ),
              pdrs: calculateDiffInPercent(
                currentForDiff.environmentalCharge.pdrsSubTotal,
                newContract.environmentalCharge.pdrsSubTotal
              ),
              reps: calculateDiffInPercent(
                currentForDiff.environmentalCharge.repsSubTotal,
                newContract.environmentalCharge.repsSubTotal
              ),
              subTotal: calculateDiffInPercent(
                currentForDiff.environmentalCharge.subTotal,
                newContract.environmentalCharge.subTotal
              )
            }
          },
          meteringAndRetailerCharge: {
            ...newContract.meteringAndRetailerCharge,
            diff: {
              meteringCharge:
                newContract.meteringAndRetailerCharge.meteringCharge -
                currentForDiff.meteringAndRetailerCharge.meteringCharge,
              retailerFee:
                newContract.meteringAndRetailerCharge.retailerFee -
                currentForDiff.meteringAndRetailerCharge.retailerFee,
              subTotal:
                newContract.meteringAndRetailerCharge.subTotal - currentForDiff.meteringAndRetailerCharge.subTotal
            },
            diffInPercent: {
              meteringCharge: calculateDiffInPercent(
                currentForDiff.meteringAndRetailerCharge.meteringCharge,
                newContract.meteringAndRetailerCharge.meteringCharge
              ),
              retailerFee: calculateDiffInPercent(
                currentForDiff.meteringAndRetailerCharge.retailerFee,
                newContract.meteringAndRetailerCharge.retailerFee
              ),
              subTotal: calculateDiffInPercent(
                currentForDiff.meteringAndRetailerCharge.subTotal,
                newContract.meteringAndRetailerCharge.subTotal
              )
            }
          },
          diff:
            sum([
              newContract.contestableCost,
              newContract.regulatedCharge.subTotal,
              newContract.networkCharge.standing,
              newContract.networkCharge.energy,
              newContract.networkCharge.demand
            ]) -
            sum([
              currentForDiff.contestableCost,
              currentForDiff.regulatedCharge.subTotal,
              currentForDiff.networkCharge.standing,
              currentForDiff.networkCharge.energy,
              currentForDiff.networkCharge.demand
            ]),
          diffInPercent: calculateDiffInPercent(
            sum([
              currentForDiff.contestableCost,
              currentForDiff.regulatedCharge.subTotal,
              currentForDiff.networkCharge.standing,
              currentForDiff.networkCharge.energy,
              currentForDiff.networkCharge.demand
            ]),
            sum([
              newContract.contestableCost,
              newContract.regulatedCharge.subTotal,
              newContract.networkCharge.standing,
              newContract.networkCharge.energy,
              newContract.networkCharge.demand
            ])
          )
        }}
      />
    );
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentVsNewTab(newValue);
  };

  return (
    <>
      <Section>
        <SectionContainer sx={CenterAlignedSectionContainerStyle}>
          <Title variant="h4" gutterBottom>
            Current Contract vs New Contract
          </Title>
          <Title variant="h5">{reportType}</Title>
          <TitleUnderline />
          <Typography variant="h5">{name}</Typography>
          <Typography variant="caption" gutterBottom component="span">
            <i>{map(groupedMeter.meters, 'meterNumber').filter(Boolean).join(', ') || '-'}</i>
          </Typography>
          <Tabs onChange={handleChange} value={currentVsNewTab} selectionFollowsFocus>
            <Tab
              label={
                <Box>
                  <Title variant="h6">Current Contract</Title>
                  <Typography variant="caption" sx={CurrentContractCaptionStyle}></Typography>
                </Box>
              }
              id="current-vs-new-tab-0"
              aria-controls="current-vs-new-tabpanel-0"
            />
            {yearSummaries.map((summary, index) => (
              <Tab
                key={`current-vs-new-tab-${index + 1}`}
                id={`current-vs-new-tab-${index + 1}`}
                label={
                  <Box sx={YearTabDateContainerStyle}>
                    <Title variant="h6">
                      {summary.tabStartFormatted} - {summary.tabEndFormatted}
                    </Title>
                  </Box>
                }
                aria-controls={`current-vs-new-tabpanel-${index + 1}`}
              />
            ))}
          </Tabs>
        </SectionContainer>
      </Section>

      <div
        role="tabpanel"
        hidden={currentVsNewTab !== 0}
        id={`current-vs-new-tabpanel-0`}
        aria-labelledby={`current-vs-new-tab-0`}
      >
        {currentVsNewTab === 0 && <BillSection data={currentContract} />}
      </div>

      {yearSummaries.length > 0 &&
        yearSummaries.map((summary, index) => (
          <div
            role="tabpanel"
            key={`current-vs-new-tabpanel-${index + 1}`}
            id={`current-vs-new-tabpanel-${index + 1}`}
            hidden={currentVsNewTab !== index + 1}
            aria-labelledby={`current-vs-new-tab-${index + 1}`}
          >
            {currentVsNewTab === index + 1 && getNewContract(summary.yearIndex)}
          </div>
        ))}
    </>
  );
};

export default CurrentVsNewBillSection;
